import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home/Home';
import Login from './pages/auth/Login';
import './App.css';
import EmployeeFilterSystem from './pages/Employee/EmployeeDirectory';
import EmployeeCheckList from './pages/Employee/EmployeeCheckList';
import AgentChat from './components/AgentChat';
import EmployeeTable from './components/EmployeeTable';
import EmployeeDocumentAccess from './pages/Employee/EmployeeDocumentAccess';
import EmployeeEditPage from './pages/Employee/EditEmployee';
import NewOnboarding from './pages/Employee/NewOnboarding';
import PersonalDataForm from './pages/E-Form';
import MainOnboardingSystem from './pages/Employee/Onboarding';
import GratuityNominationForm, { FormF } from './pages/NewFormF';
import EPFForm11 from './pages/PFForm11';
import AllEforms from './pages/AllEforms';
import JoiningReportForm from './pages/JoinigReportForm';
import EmployeeProfile from './pages/employeeProfile/Profile';
import EmployeeAuth from './pages/employeeProfile/EmpAuth';
import EmployeeLayout from './components/EmployeeLayout';
import HROnboardingPanel from './pages/HROnboardingPanel';
import IDCardForm from './pages/IDCardForm';
import DocumentsUpload from './pages/employeeProfile/DocumentsUpload';
import UserManagement from './components/UserManagement';
import ForgotPassword from './components/ForgotPassword';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.right)) {
    // Redirect to appropriate dashboard based on role
    if (user.right === 'ADMIN') {
      return <Navigate to="/" replace />;
    } else if (user.right === 'EMPLOYEE') {
      return <Navigate to="/employee" replace />;
    }
    logout();
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Root redirect component
const RootRedirect = () => {
  const { user, isAuthenticated, logout } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to appropriate dashboard based on role
  if (user?.right === 'ADMIN') {
    return <Navigate to="/" replace />;
  } else if (user?.right === 'EMPLOYEE') {
    return <Navigate to="/employee" replace />;
  }
  logout();
  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Routes>
            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />
            
            {/* Public Routes */}
            <Route path="/login" element={<EmployeeAuth />} />
            <Route path="/emp-auth" element={<EmployeeAuth />} />
            <Route path="/e-form" element={<PersonalDataForm />} />
            <Route path="/form-f" element={<FormF />} />
            <Route path="/form-f1" element={<FormF />} />
            <Route path="/form-11" element={<EPFForm11 />} />
            <Route path="/joining-report" element={<JoiningReportForm />} />
            <Route path="/id-card" element={<IDCardForm />} />
            <Route path="/user-management" element={<UserManagement />} />

            {/* Forgot password route */}
            <Route path="/reset-password/:resetPasswordSession" element={<ForgotPassword />} />

            {/* Admin Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Home />} />
              <Route path="agent-chat" element={<AgentChat agentType="CeramIQ" />} />
              <Route path="checklist" element={<EmployeeCheckList />} />
              <Route path="checklist/inactive" element={<EmployeeCheckList />} />
              <Route path="employees" element={<EmployeeFilterSystem />} />
              <Route path="employees/inactive" element={<EmployeeFilterSystem />} />
              <Route path="employee-table" element={<EmployeeTable />} />
              <Route path="new-onboarding" element={<NewOnboarding />} />
              <Route path="onboarding" element={<MainOnboardingSystem />} />
              <Route path="employee-edit/:employeeCode" element={<EmployeeEditPage />} />
              <Route path="employee-document-access/:employeeCode" element={<EmployeeDocumentAccess />} />
              <Route path="eforms" element={<AllEforms />} />
              <Route path="hr-onboarding-panel" element={<HROnboardingPanel />} />
            </Route>

            {/* Employee Routes */}
            <Route
              path="/employee"
              element={
                <ProtectedRoute allowedRoles={['EMPLOYEE']}>
                  <EmployeeLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<EmployeeProfile />} />
              <Route path="edit/:employeeCode" element={<EmployeeEditPage />} />
              <Route path="document-access/:employeeCode" element={<EmployeeDocumentAccess />} />
              <Route path="documents-upload/:employeeCode" element={<DocumentsUpload />}/>
              <Route path="e-form" element={<PersonalDataForm />} />
              <Route path="form-f" element={<FormF />} />
              <Route path="form-f1" element={<FormF />} />
              <Route path="form-11" element={<EPFForm11 />} />
              <Route path="joining-report" element={<JoiningReportForm />} />
              <Route path="id-card" element={<IDCardForm />} />

              {/* Add more employee-only pages here */}
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;